import { httpService } from './api/http';
import {
    toPublicProfileCoverUrl,
    toPublicProfilePictureUrl,
} from '../utils/mediaUrl';
import {
    buildProfilePreferredStyles,
    formatProfilePreferredStyles,
} from '../utils/profilePreferredStyles';
import { CANONICAL_VISIBILITY, parseCanonicalVisibility } from '../constants/visibility';
import { resolveGatewayBackedServiceBaseUrl } from './serviceBaseUrl';

const USER_SERVICE_URL = resolveGatewayBackedServiceBaseUrl(process.env.REACT_APP_USER_SERVICE_URL);
const OBJECT_SERVICE_URL = resolveGatewayBackedServiceBaseUrl(process.env.REACT_APP_OBJECT_SERVICE_URL);
const MEDIA_SERVICE_URL = resolveGatewayBackedServiceBaseUrl(process.env.REACT_APP_MEDIA_SERVICE_URL);

const PROFILE_PICTURE_PATH_SEGMENT = '/api/media/profile-picture';
const PROFILE_COVER_PATH_SEGMENT = '/api/media/profile-cover';
const MUSIC_STYLE_PATH_SEGMENT = '/api/reference/music-styles';
const EXPECTED_MISSING_MEDIA_STATUSES = [404];

const AUTHENTICATED_REQUEST_OPTIONS = {
    credentials: 'include',
};

const MULTIPART_REQUEST_OPTIONS = {
    credentials: 'include',
    headers: {
        Accept: 'application/json',
    },
};

const BLOB_REQUEST_OPTIONS = {
    credentials: 'include',
    headers: {
        'Content-Type': undefined,
    },
    responseType: 'blob',
    suppressErrorStatusLogs: EXPECTED_MISSING_MEDIA_STATUSES,
};

const PUBLIC_REQUEST_OPTIONS = {
    credentials: 'omit',
    skipAuthRefresh: true,
    suppressErrorStatusLogs: EXPECTED_MISSING_MEDIA_STATUSES,
};

const OPTIONAL_MEDIA_REQUEST_OPTIONS = {
    ...AUTHENTICATED_REQUEST_OPTIONS,
    suppressErrorStatusLogs: EXPECTED_MISSING_MEDIA_STATUSES,
};

const mergeRequestOptions = (baseOptions, options = {}) => ({
    ...baseOptions,
    ...options,
});

const normalizeProfileMediaUrl = (url, pathSegment) => {
    if (!url) {
        return null;
    }
    const trimmed = String(url).trim();
    if (!trimmed) {
        return null;
    }
    if (trimmed.startsWith('/')) {
        return trimmed;
    }
    try {
        const parsed = new URL(trimmed);
        if (parsed.pathname.includes(pathSegment)) {
            return `${parsed.pathname}${parsed.search}`;
        }
    } catch {
        return trimmed;
    }
    return trimmed;
};

const normalizeProfileMediaPayload = (payload, pathSegment) => {
    if (!payload) {
        return payload;
    }
    if (Array.isArray(payload)) {
        return payload.map((item) => {
            if (!item || typeof item !== 'object' || !('url' in item)) {
                return item;
            }
            const normalizedUrl = normalizeProfileMediaUrl(item.url, pathSegment);
            if (normalizedUrl === item.url) {
                return item;
            }
            return { ...item, url: normalizedUrl };
        });
    }
    if (typeof payload === 'object' && 'url' in payload) {
        const normalizedUrl = normalizeProfileMediaUrl(payload.url, pathSegment);
        if (normalizedUrl === payload.url) {
            return payload;
        }
        return { ...payload, url: normalizedUrl };
    }
    return payload;
};

const normalizeProfilePicturePayload = (payload) => (
    normalizeProfileMediaPayload(payload, PROFILE_PICTURE_PATH_SEGMENT)
);

const normalizeProfileCoverPayload = (payload) => (
    normalizeProfileMediaPayload(payload, PROFILE_COVER_PATH_SEGMENT)
);

const trimToNull = (value) => {
    if (typeof value !== 'string') {
        return null;
    }
    const trimmed = value.trim();
    return trimmed.length > 0 ? trimmed : null;
};

const normalizeProfileCount = (value) => {
    if (value === null || value === undefined || value === '') {
        return null;
    }
    const numeric = Number(value);
    if (!Number.isSafeInteger(numeric) || numeric < 0) {
        return null;
    }
    return numeric;
};

const buildLocationDisplay = ({ city, stateOrRegion, country }) => {
    const parts = [city, stateOrRegion, country]
        .map((part) => (typeof part === 'string' ? part.trim() : ''))
        .filter((part) => part.length > 0);
    return parts.length > 0 ? parts.join(', ') : null;
};

const normalizeProfileVisibility = (value, { required = false } = {}) => {
    const normalized = parseCanonicalVisibility(value);
    if (normalized && (!required || normalized === CANONICAL_VISIBILITY.PUBLIC || normalized === CANONICAL_VISIBILITY.PRIVATE)) {
        return normalized;
    }
    if (required) {
        throw new Error('visibility must be PUBLIC or PRIVATE');
    }
    return CANONICAL_VISIBILITY.PUBLIC;
};

const toProfileSummary = (uomProfile, userId, { publicMedia = false } = {}) => {
    const metadata = uomProfile?.metadata || {};
    const metadataLocation = trimToNull(metadata.location) || trimToNull(metadata.address);
    const city = trimToNull(metadata.city);
    const stateOrRegion = trimToNull(metadata.stateOrRegion);
    const country = trimToNull(metadata.country);
    const hasCoordinate = typeof uomProfile?.coordinate?.latitude === 'number'
        && typeof uomProfile?.coordinate?.longitude === 'number';
    const location = (metadataLocation || city || stateOrRegion || country || hasCoordinate)
        ? {
            city,
            stateOrRegion,
            country,
            latitude: hasCoordinate ? uomProfile.coordinate.latitude : null,
            longitude: hasCoordinate ? uomProfile.coordinate.longitude : null,
            formatted: metadataLocation,
        }
        : null;
    const locationDisplay = metadataLocation || buildLocationDisplay({
        city,
        stateOrRegion,
        country,
    });

    const avatar = metadata.avatar || metadata.avatarUrl || metadata.profilePicture;
    const cover = metadata.cover || metadata.coverUrl || metadata.profileCover;
    const preferredStyles = buildProfilePreferredStyles(metadata);

    return {
        userId: uomProfile?.createdBy || userId,
        objectId: uomProfile?.objectId || uomProfile?.id || null,
        visibility: normalizeProfileVisibility(uomProfile?.privacy || uomProfile?.visibility),
        privacy: normalizeProfileVisibility(uomProfile?.privacy || uomProfile?.visibility),
        profile: {
            displayName: uomProfile?.name,
            location,
            locationDisplay: locationDisplay || null,
            about: metadata.about || null,
            influences: metadata.influences || null,
            equipment: metadata.equipment || null,
            preferredStyle: formatProfilePreferredStyles(preferredStyles) || null,
            preferredStyles,
            instrumentExperience: metadata.instrumentExperience || null,
            commitment: metadata.commitment || null,
            avatar: publicMedia
                ? toPublicProfilePictureUrl(avatar)
                : normalizeProfileMediaUrl(avatar, PROFILE_PICTURE_PATH_SEGMENT),
            cover: publicMedia
                ? toPublicProfileCoverUrl(cover)
                : normalizeProfileMediaUrl(cover, PROFILE_COVER_PATH_SEGMENT),
            playsCount: normalizeProfileCount(metadata.playsCount),
            seeking: metadata.seeking || null,
        },
        updatedAt: uomProfile?.updatedAt ? new Date(uomProfile.updatedAt) : null,
        createdAt: uomProfile?.createdAt ? new Date(uomProfile.createdAt) : null,
    };
};

const isNotFoundError = (error) => (
    error?.status === 404 || error?.response?.status === 404
);

export const userProfileService = {
    // Public profile operations (Object Service - UOM)
    getProfile: async (userId, options = {}) => {
        try {
            // Use new object-service endpoint for UOM profile
            const response = await httpService.get(
                `${OBJECT_SERVICE_URL}/api/objects/v1/users/${userId}/profile`,
                mergeRequestOptions(AUTHENTICATED_REQUEST_OPTIONS, options)
            );
            return toProfileSummary(response, userId);
        } catch (error) {
            console.error('Error fetching profile from object-service:', error);
            throw error;
        }
    },

    getInternetProfile: async (userId, options = {}) => {
        try {
            const response = await httpService.get(
                `${OBJECT_SERVICE_URL}/api/objects/v1/public/users/${userId}/profile`,
                mergeRequestOptions(AUTHENTICATED_REQUEST_OPTIONS, options)
            );
            return toProfileSummary(response, userId, { publicMedia: true });
        } catch (error) {
            console.error('Error fetching internet profile from object-service:', error);
            throw error;
        }
    },

    updateProfileVisibility: async (userId, visibility) => {
        const normalizedVisibility = normalizeProfileVisibility(visibility, { required: true });
        try {
            return await httpService.patch(
                `${OBJECT_SERVICE_URL}/api/objects/v1/users/${userId}/visibility`,
                { visibility: normalizedVisibility },
                AUTHENTICATED_REQUEST_OPTIONS
            );
        } catch (error) {
            console.error('Error updating profile visibility:', error);
            throw error;
        }
    },

    // Owner/admin profile operations (User Service)
    getOwnProfile: async (userId, options = {}) => {
        try {
            return await httpService.get(
                `${USER_SERVICE_URL}/api/users/${userId}/profile`,
                mergeRequestOptions(AUTHENTICATED_REQUEST_OPTIONS, options)
            );
        } catch (error) {
            console.error('Error fetching owner profile from user-service:', error);
            throw error;
        }
    },

    updateProfile: async (userId, profileData, options = {}) => {
        try {
            return await httpService.put(
                `${USER_SERVICE_URL}/api/users/${userId}/profile`,
                profileData,
                mergeRequestOptions(AUTHENTICATED_REQUEST_OPTIONS, options)
            );
        } catch (error) {
            console.error('Error updating profile:', error);
            throw error;
        }
    },
    updateLocation: async (userId, locationPayload) => {
        try {
            return await httpService.put(
                `${USER_SERVICE_URL}/api/users/${userId}/profile`,
                {
                    profile: {
                        location: locationPayload.location ?? null,
                        locationDisplay: locationPayload.locationDisplay ?? null,
                    }
                },
                AUTHENTICATED_REQUEST_OPTIONS
            );
        } catch (error) {
            console.error('Error updating location:', error);
            throw error;
        }
    },

    resolveLocationByCoordinates: async ({ latitude, longitude, locale }) => {
        try {
            return await httpService.post(
                `${USER_SERVICE_URL}/api/users/location/resolve`,
                {
                    latitude,
                    longitude,
                    locale,
                },
                AUTHENTICATED_REQUEST_OPTIONS
            );
        } catch (error) {
            console.error('Error resolving location:', error);
            throw error;
        }
    },

    updateStatus: async (userId, statusData) => {
        try {
            return await httpService.patch(
                `${USER_SERVICE_URL}/api/users/${userId}/profile/status`,
                statusData,
                AUTHENTICATED_REQUEST_OPTIONS
            );
        } catch (error) {
            console.error('Error updating status:', error);
            throw error;
        }
    },

    updateSettings: async (userId, settingsData) => {
        try {
            return await httpService.patch(
                `${USER_SERVICE_URL}/api/users/${userId}/profile/settings`,
                settingsData,
                AUTHENTICATED_REQUEST_OPTIONS
            );
        } catch (error) {
            console.error('Error updating settings:', error);
            throw error;
        }
    },

    deleteProfile: async (userId) => {
        try {
            await httpService.delete(
                `${USER_SERVICE_URL}/api/users/${userId}/profile`,
                AUTHENTICATED_REQUEST_OPTIONS
            );
        } catch (error) {
            console.error('Error deleting profile:', error);
            throw error;
        }
    },

    listPreferredStyles: async (query) => {
        try {
            const options = query && query.trim()
                ? { ...AUTHENTICATED_REQUEST_OPTIONS, params: { q: query.trim() } }
                : AUTHENTICATED_REQUEST_OPTIONS;
            return await httpService.get(
                `${USER_SERVICE_URL}${MUSIC_STYLE_PATH_SEGMENT}`,
                options
            );
        } catch (error) {
            console.error('Error fetching preferred styles:', error);
            throw error;
        }
    },

    // Media operations (Media Service)
    uploadProfilePicture: async (formData) => {
        try {
            const response = await httpService.post(
                `${MEDIA_SERVICE_URL}/api/media/profile-picture`,
                formData,
                MULTIPART_REQUEST_OPTIONS
            );
            // Normalize URLs here to keep hooks/components deterministic.
            return normalizeProfilePicturePayload(response);
        } catch (error) {
            console.error('Error uploading profile picture:', error);
            throw error;
        }
    },

    getProfilePicture: async (userId) => {
        try {
            return await httpService.get(
                `${MEDIA_SERVICE_URL}/api/media/profile-picture/${userId}`,
                BLOB_REQUEST_OPTIONS
            );
        } catch (error) {
            if (isNotFoundError(error)) {
                return null;
            }
            console.error('Error getting profile picture:', error);
            throw error;
        }
    },

    getProfilePictureUrl: async (userId) => {
        try {
            const response = await httpService.get(
                `${MEDIA_SERVICE_URL}/api/media/profile-picture/url/${userId}`,
                OPTIONAL_MEDIA_REQUEST_OPTIONS
            );
            // Normalize URLs here to keep hooks/components deterministic.
            return normalizeProfilePicturePayload(response);
        } catch (error) {
            if (isNotFoundError(error)) {
                return null;
            }
            console.error('Error getting profile picture URL:', error);
            throw error;
        }
    },

    getPublicProfilePictureUrl: async (userId) => {
        try {
            const response = await httpService.get(
                `${MEDIA_SERVICE_URL}/api/media/public/profile-picture/url/${userId}`,
                PUBLIC_REQUEST_OPTIONS
            );
            return normalizeProfilePicturePayload(response);
        } catch (error) {
            if (isNotFoundError(error)) {
                return null;
            }
            console.error('Error getting public profile picture URL:', error);
            throw error;
        }
    },

    getPublicProfileCoverUrl: async (userId) => {
        try {
            const response = await httpService.get(
                `${MEDIA_SERVICE_URL}/api/media/public/profile-cover/url/${userId}`,
                PUBLIC_REQUEST_OPTIONS
            );
            return normalizeProfileCoverPayload(response);
        } catch (error) {
            if (isNotFoundError(error)) {
                return null;
            }
            console.error('Error getting public profile cover URL:', error);
            throw error;
        }
    },

    getUserPhotos: async () => {
        try {
            const response = await httpService.get(
                `${MEDIA_SERVICE_URL}/api/media/profile-picture/history`,
                OPTIONAL_MEDIA_REQUEST_OPTIONS
            );
            return normalizeProfilePicturePayload(response);
        } catch (error) {
            if (isNotFoundError(error)) {
                return [];
            }
            console.error('Error fetching user photos:', error);
            throw error;
        }
    },

    useExistingProfilePicture: async (objectKey) => {
        try {
            const response = await httpService.post(
                `${MEDIA_SERVICE_URL}/api/media/profile-picture/select`,
                { objectKey },
                AUTHENTICATED_REQUEST_OPTIONS
            );
            return normalizeProfilePicturePayload(response);
        } catch (error) {
            console.error('Error selecting existing profile picture:', error);
            throw error;
        }
    },

    deleteProfilePhoto: async (objectKey) => {
        try {
            const response = await httpService.delete(
                `${MEDIA_SERVICE_URL}/api/media/profile-picture/object`,
                { ...AUTHENTICATED_REQUEST_OPTIONS, params: { objectKey } }
            );
            return normalizeProfilePicturePayload(response);
        } catch (error) {
            console.error('Error deleting profile photo:', error);
            throw error;
        }
    },

    deleteProfilePicture: async () => {
        try {
            await httpService.delete(
                `${MEDIA_SERVICE_URL}/api/media/profile-picture`,
                AUTHENTICATED_REQUEST_OPTIONS
            );
        } catch (error) {
            console.error('Error deleting profile picture:', error);
            throw error;
        }
    },

    uploadProfileCover: async (formData) => {
        try {
            const response = await httpService.post(
                `${MEDIA_SERVICE_URL}/api/media/profile-cover`,
                formData,
                MULTIPART_REQUEST_OPTIONS
            );
            return normalizeProfileCoverPayload(response);
        } catch (error) {
            console.error('Error uploading profile cover:', error);
            throw error;
        }
    },

    getProfileCover: async (userId) => {
        try {
            return await httpService.get(
                `${MEDIA_SERVICE_URL}/api/media/profile-cover/${userId}`,
                BLOB_REQUEST_OPTIONS
            );
        } catch (error) {
            if (isNotFoundError(error)) {
                return null;
            }
            console.error('Error getting profile cover:', error);
            throw error;
        }
    },

    getProfileCoverUrl: async (userId) => {
        try {
            const response = await httpService.get(
                `${MEDIA_SERVICE_URL}/api/media/profile-cover/url/${userId}`,
                OPTIONAL_MEDIA_REQUEST_OPTIONS
            );
            return normalizeProfileCoverPayload(response);
        } catch (error) {
            if (isNotFoundError(error)) {
                return null;
            }
            console.error('Error getting profile cover URL:', error);
            throw error;
        }
    },

    getUserCovers: async () => {
        try {
            const response = await httpService.get(
                `${MEDIA_SERVICE_URL}/api/media/profile-cover/history`,
                OPTIONAL_MEDIA_REQUEST_OPTIONS
            );
            return normalizeProfileCoverPayload(response);
        } catch (error) {
            if (isNotFoundError(error)) {
                return [];
            }
            console.error('Error fetching user covers:', error);
            throw error;
        }
    },

    useExistingProfileCover: async (objectKey) => {
        try {
            const response = await httpService.post(
                `${MEDIA_SERVICE_URL}/api/media/profile-cover/select`,
                { objectKey },
                AUTHENTICATED_REQUEST_OPTIONS
            );
            return normalizeProfileCoverPayload(response);
        } catch (error) {
            console.error('Error selecting existing profile cover:', error);
            throw error;
        }
    },

    deleteProfileCoverPhoto: async (objectKey) => {
        try {
            const response = await httpService.delete(
                `${MEDIA_SERVICE_URL}/api/media/profile-cover/object`,
                { ...AUTHENTICATED_REQUEST_OPTIONS, params: { objectKey } }
            );
            return normalizeProfileCoverPayload(response);
        } catch (error) {
            console.error('Error deleting profile cover:', error);
            throw error;
        }
    },

    deleteProfileCover: async () => {
        try {
            await httpService.delete(
                `${MEDIA_SERVICE_URL}/api/media/profile-cover`,
                AUTHENTICATED_REQUEST_OPTIONS
            );
        } catch (error) {
            console.error('Error deleting current profile cover:', error);
            throw error;
        }
    }
};
